// File: src/components/artistDashboard/FanFunnel.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// 🌟 Import ComingSoonModal
import ComingSoonModal from '../miscellaneous/ComingSoonModal';

// Streamlined 6-stage funnel configuration matching your updated data tracks
const funnelStages = [
    { label: "Hot Raters", dataKey: 'uniqueHotRaters', color: 'bg-rose-600', widthClass: 'w-full' },
    { label: "Playlisters", dataKey: 'uniquePlaylisters', color: 'bg-orange-500', widthClass: 'w-11/12' },
    { label: "Subscribers", dataKey: 'subscribers', color: 'bg-yellow-500', widthClass: 'w-10/12' },
    { label: "Fans", dataKey: 'uniqueTippers', color: 'bg-green-500', widthClass: 'w-9/12' },
    { label: "Supafans", dataKey: 'supaFans', color: 'bg-blue-500', widthClass: 'w-8/12' },
    { label: "Ultrafans", dataKey: 'ultraFans', color: 'bg-[#ff00cc]', widthClass: 'w-8/12' } 
];

const FunnelStage = ({ label, value, color, widthClass, onClick, isUltra }) => (
    /* Added pr-16 to make sure the row outer bounds reserve structural memory space for the floating buttons */
    <div className={`mx-auto relative ${widthClass} mb-2 pr-16 sm:pr-20`}>
        
        {/* 📊 CORE FUNNEL SEGMENT */}
        <div 
            className={`w-full text-white py-3.5 pl-8 pr-10 ${color} rounded-md shadow-md flex justify-between items-center`}
            style={{
                // 🌟 CONDITION: Symmetrical slope for upper tiers, completely square rectangle for Ultrafans
                clipPath: isUltra 
                    ? 'none' 
                    : 'polygon(0 0, 100% 0, 96% 100%, 4% 100%)'
            }}
        >
            <span className="text-xs font-black uppercase tracking-widest truncate">{label}</span>
            <span className="text-base font-black tabular-nums">{value.toLocaleString()}</span>
        </div>

        {/* 🎯 OVERLAY TARGET PILL: Pushed safely outside to the far right gutter space */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation(); 
                    onClick();
                }}
                className="pointer-events-auto px-4 sm:px-5 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-full bg-slate-950/90 text-white border border-[#ff00cc]/50 shadow-lg hover:bg-slate-900 hover:border-[#ff00cc] active:scale-95 transition-all duration-150 backdrop-blur-xs flex items-center justify-center min-w-[76px] sm:min-w-[84px]"
            >
                Target
            </button>
        </div>
    </div>
);

const FanFunnel = ({ data, onStageClick }) => {
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    
    // 🌟 State for ComingSoonModal
    const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);

    if (!data) return null;

    // Temporary handler to open ComingSoonModal instead of onStageClick
    const handleTargetClick = (stageKey) => {
        setIsComingSoonOpen(true);
        // Note: You can still call onStageClick(stageKey) here in the future when re-enabling
    };

    return (
        <div className="p-4 sm:p-6 bg-white dark:bg-slate-950 rounded-3xl border border-slate-100 dark:border-slate-800/80 shadow-sm dark:shadow-2xl max-w-2xl mx-auto overflow-visible relative transition-colors duration-200">
            
            {/* CARD HEADER WITH INTEGRATED INFO TRIGGER */}
            <div className="relative flex items-center justify-center mb-6">
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    My Fan Funnel
                </h3>
                <button
                    onClick={() => setIsInfoModalOpen(true)}
                    className="absolute right-0 p-1 rounded-full text-slate-400 hover:text-[#ff00cc] transition-colors focus:outline-none flex items-center justify-center"
                    title="Funnel Strategy & Mechanics"
                >
                    <span className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center text-[10px] font-black">i</span>
                </button>
            </div>
            
            {/* FUNNEL STAGES */}
            <div className="flex flex-col">
                {funnelStages.map(stage => (
                    <FunnelStage 
                        key={stage.dataKey}
                        label={stage.label}
                        value={data[stage.dataKey] ?? 0}
                        color={stage.color}
                        widthClass={stage.widthClass}
                        onClick={() => handleTargetClick(stage.dataKey)}
                        isUltra={stage.dataKey === 'ultraFans'}
                    />
                ))}
            </div>

            {/* 🌟 STRATEGY & ARCHITECTURE EXPLAINER MODAL SHEET */}
            <AnimatePresence>
                {isInfoModalOpen && (
                    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-150">
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 10 }}
                            className="bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 max-w-md w-full rounded-2xl p-6 shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Sticky Modal Title Block */}
                            <div className="border-b dark:border-zinc-800 pb-3 flex justify-between items-center flex-shrink-0">
                                <div>
                                    <h4 className="text-xs font-mono font-black uppercase tracking-widest text-brand-pink">Rockstar Blueprint</h4>
                                    <h3 className="text-base font-black uppercase tracking-tight dark:text-white mt-0.5">Fan Funnel Framework</h3>
                                </div>
                                <button 
                                    onClick={() => setIsInfoModalOpen(false)}
                                    className="w-7 h-7 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center font-bold text-xs text-slate-500 dark:text-zinc-400 hover:text-brand-pink dark:hover:text-brand-pink transition-colors focus:outline-none"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Scrollable Architectural Content Core */}
                            <div className="flex-grow overflow-y-auto py-4 space-y-5 text-xs font-semibold leading-relaxed text-slate-600 dark:text-zinc-400 custom-scrollbar pr-1">
                                
                                {/* Section A: The Capture Phase */}
                                <section className="space-y-1.5">
                                    <h5 className="text-[10px] font-black uppercase tracking-wider text-slate-900 dark:text-zinc-200 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-600" />
                                        1. Funnel Discovery (Top of Funnel)
                                    </h5>
                                    <p className="pl-3">
                                        New listeners discovering you through <strong className="dark:text-white">Trends</strong>, direct profile traffic, and in-app <strong className="dark:text-white">Radio</strong>. These listeners mark tracks <strong className="dark:text-white">"Hot"</strong> signalling prospective interest.
                                    </p>
                                </section>

                                {/* Section B: Anchor Retention */}
                                <section className="space-y-1.5">
                                    <h5 className="text-[10px] font-black uppercase tracking-wider text-slate-900 dark:text-zinc-200 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                                        2. Playlisting & Custom Compilations
                                    </h5>
                                    <p className="pl-3">
                                        Listeners turn into repeat fans by saving tracks into their private libraries / custom <strong className="dark:text-white">Playlists</strong>.
                                    </p>
                                </section>

                                {/* Section C: Financial Accumulation */}
                                <section className="space-y-1.5">
                                    <h5 className="text-[10px] font-black uppercase tracking-wider text-slate-900 dark:text-zinc-200 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                                        3. Subscribed to Updates
                                    </h5>
                                    <p className="pl-3">
                                        These viewers recieve in-app "pings" when you release new announcements in the <strong className="dark:text-white">Press Feed</strong>. Strong contenders to <strong className="dark:text-white">Tip</strong> Fan Points to acquire exclusive Fan-Only perks.
                                    </p>
                                </section>

                                {/* Section D: The Three Core Financial Tiers */}
                                <section className="space-y-2 pt-2 border-t dark:border-zinc-800/60">
                                    <h5 className="text-[10px] font-black uppercase tracking-wider text-[#ff00cc]">
                                        Dynamic Supporter Threshold Classifications
                                    </h5>
                                    
                                    <div className="space-y-2 pl-1">
                                        <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-zinc-950/40 border dark:border-zinc-800 flex gap-2">
                                            <span className="text-[10px] font-black text-green-500 uppercase bg-green-500/5 px-2 py-0.5 rounded-md border border-green-500/10 h-fit flex-shrink-0">Fan</span>
                                            <p className="text-[11px] leading-snug">
                                                <strong className="dark:text-white">Score Tier: 250 pts – 24,999 pts</strong><br/>
                                            </p>
                                        </div>

                                        <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-zinc-950/40 border dark:border-zinc-800 flex gap-2">
                                            <span className="text-[10px] font-black text-blue-500 uppercase bg-blue-500/5 px-2 py-0.5 rounded-md border border-blue-500/10 h-fit flex-shrink-0">SupaFan</span>
                                            <p className="text-[11px] leading-snug">
                                                <strong className="dark:text-white">Score Tier: 25,000 pts – 249,999 pts</strong><br/>
                                            </p>
                                        </div>

                                        <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-zinc-950/40 border dark:border-zinc-800 flex gap-2">
                                            <span className="text-[10px] font-black text-[#ff00cc] uppercase bg-[#ff00cc]/5 px-2 py-0.5 rounded-md border border-[#ff00cc]/10 h-fit flex-shrink-0">UltraFan</span>
                                            <p className="text-[11px] leading-snug">
                                                <strong className="dark:text-white">Score Tier: 250,000+ pts</strong><br/>
                                            </p>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            {/* Sticky Modal Close Trigger */}
                            <div className="pt-3 border-t dark:border-zinc-800 flex-shrink-0">
                                <button 
                                    onClick={() => setIsInfoModalOpen(false)}
                                    className="w-full py-3 bg-slate-900 dark:bg-zinc-800 hover:bg-black dark:hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md active:scale-98"
                                >
                                    Got it!
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* 🌟 COMING SOON MODAL (Temporarily wired to all Target buttons) */}
            <ComingSoonModal 
                isOpen={isComingSoonOpen} 
                onClose={() => setIsComingSoonOpen(false)} 
            />
        </div>
    );
};

export default FanFunnel;